<script setup lang="ts">
  import { onMounted } from 'vue';
  import { SearchCombobox, SelectInput, SimpleLoading } from '@/shared/ui';
  import { toNumberOrUndefined } from '@/shared/lib';
  import { buildTrainingMatrixRevisionOptionLabel } from '../../lib/buildTrainingMatrixRevisionOptionLabel';
  import type { useOnboardingForm } from '../../model/useOnboardingForm';
  import CreateTrainingDocumentsTable from '../CreateTrainingDocumentsTable.vue';
  import TrainingScheduledDateField from '../TrainingScheduledDateField.vue';
  import TrainingMinistrantesSection from '../TrainingMinistrantesSection.vue';
  import TrainingNotesField from '../TrainingNotesField.vue';

  const props = defineProps<{
    form: ReturnType<typeof useOnboardingForm>;
  }>();

  onMounted(async () => {
    await props.form.load();
  });
</script>

<template>
  <div
    v-if="form.loading.value"
    data-testid="onboarding-form-loading"
    class="flex w-full justify-center py-12"
  >
    <SimpleLoading />
  </div>

  <template v-else>
    <div
      data-testid="onboarding-form"
      class="flex flex-col gap-1.5 border-b border-gray-02 px-6 py-8"
    >
      <span
        id="onboarding-title-label"
        class="text-sm font-semibold text-gray-07"
        >Título do Treinamento:</span
      >
      <input
        :value="form.title.value"
        type="text"
        data-testid="onboarding-title-input"
        aria-labelledby="onboarding-title-label"
        placeholder="Escreva um título para identificar o treinamento"
        class="w-full rounded-lg border border-gray-02 px-4 py-3 text-sm text-gray-07 shadow-xs placeholder:text-gray-03 focus:border-gray-07 focus:outline-none"
        @input="form.title.value = ($event.target as HTMLInputElement).value"
      />
    </div>

    <div class="flex flex-col gap-3.5 px-6 py-8">
      <span class="text-sm font-semibold text-gray-07">Programação do Treinamento</span>
      <div class="grid grid-cols-[304px_348px_348px] gap-4">
        <div class="flex flex-col gap-1.5">
          <span
            id="onboarding-scheduled-by-member-label"
            class="text-sm font-semibold text-gray-07"
            >Responsável pela Programação:</span
          >
          <SelectInput
            :model-value="form.scheduledByMemberId.value ?? ''"
            :options="form.memberOptions.value"
            label="Selecione um colaborador"
            aria-labelledby="onboarding-scheduled-by-member-label"
            @update:model-value="form.setScheduledByMemberId(toNumberOrUndefined($event) ?? null)"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <span
            id="onboarding-scheduled-by-position-label"
            class="text-sm font-semibold text-gray-07"
            >Cargo:</span
          >
          <SelectInput
            :model-value="form.scheduledByPositionId.value ?? ''"
            :options="form.scheduledByPositions.value"
            label="Selecione o cargo relacionado ao treinamento"
            aria-labelledby="onboarding-scheduled-by-position-label"
            @update:model-value="form.scheduledByPositionId.value = toNumberOrUndefined($event) ?? null"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <span
            id="onboarding-scheduled-date-label"
            class="text-sm font-semibold text-gray-07"
            >Data da programação:</span
          >
          <TrainingScheduledDateField
            :model-value="form.scheduledDate.value"
            testid="onboarding-scheduled-date-trigger"
            labelledby="onboarding-scheduled-date-label"
            @update:model-value="form.scheduledDate.value = $event"
          />
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-4 border-b border-gray-02 px-6 pb-8">
      <div class="grid grid-cols-[304px_348px_348px] gap-4">
        <div class="flex flex-col gap-1.5">
          <span
            id="onboarding-trainee-member-label"
            class="text-sm font-semibold text-gray-07"
            >Colaborador a ser treinado:</span
          >
          <SelectInput
            :model-value="form.traineeMemberId.value ?? ''"
            :options="form.memberOptions.value"
            label="Selecione um colaborador"
            aria-labelledby="onboarding-trainee-member-label"
            @update:model-value="form.setTraineeMemberId(toNumberOrUndefined($event) ?? null)"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <span
            id="onboarding-trainee-position-label"
            class="text-sm font-semibold text-gray-07"
            >Cargo:</span
          >
          <SelectInput
            :model-value="form.traineePositionId.value ?? ''"
            :options="form.traineePositions.value"
            label="Selecione o cargo relacionado ao treinamento"
            aria-labelledby="onboarding-trainee-position-label"
            @update:model-value="form.traineePositionId.value = toNumberOrUndefined($event) ?? null"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <span
            id="onboarding-matrix-revision-label"
            class="text-sm font-semibold text-gray-07"
            >Matriz de treinamento:</span
          >
          <SearchCombobox
            :model-value="form.trainingMatrixRevisionId.value ? String(form.trainingMatrixRevisionId.value) : ''"
            :options="
              form.revisions.value.map((revision) => ({
                value: String(revision.id),
                label: buildTrainingMatrixRevisionOptionLabel(revision),
              }))
            "
            placeholder="Selecione uma revisão"
            trigger-icon="arrow-down"
            aria-labelledby="onboarding-matrix-revision-label"
            class="h-11 rounded-lg border-[var(--color-border-color)] bg-white-01 px-4 py-2.5 shadow-none"
            @update:model-value="form.trainingMatrixRevisionId.value = toNumberOrUndefined($event) ?? null"
          />
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-4 border-b border-gray-02 px-6 py-8">
      <span class="text-sm font-semibold text-gray-07">Documentos</span>
      <CreateTrainingDocumentsTable
        :documents="form.documents.documents.value"
        :loading="form.documents.loading.value"
        @view="form.documents.viewDocumentRevision"
      />
    </div>

    <TrainingMinistrantesSection
      :ministrantes="form.ministrantes"
      :members="form.members.value"
      :member-options="form.memberOptions.value"
    />

    <TrainingNotesField
      :model-value="form.notes.value"
      testid="onboarding-notes-input"
      @update:model-value="form.notes.value = $event"
    />
  </template>
</template>
